import { lazy } from 'react';
import CertificateIcon from 'features/certificate/components/CertificateIcon';
import { CERTIFICATE_OPEN_PARAM, MAX_CERTIFICATE_ACCOUNTS } from 'features/certificate/constants';
import { HINT_TARGETS, LABELS, LEVELS } from '../constants';
import type { PostContent } from '../types';

type Heroes = typeof import('./heroes/certificates');
// The four pictures share one chunk, loaded when the popup first shows this post.
const hero = (name: keyof Heroes) => lazy(() => import('./heroes/certificates').then((heroes) => ({ default: heroes[name] })));

export const certificates: PostContent = {
  id: '2026-10-certificates',
  level: LEVELS.launch,
  label: LABELS.new,
  area: 'Dashboard',
  feature: 'Performance certificates',
  icon: CertificateIcon,
  title: 'Share your verified trading results',
  summary: `Turn the closed trades on up to ${MAX_CERTIFICATE_ACCOUNTS} accounts into a performance certificate with its own public link.`,
  steps: [
    {
      label: 'Overview',
      title: 'Share your verified trading results',
      body: `Turn the closed trades on up to ${MAX_CERTIFICATE_ACCOUNTS} accounts into a performance certificate, verified by TradersConnect, with a public link of its own.`,
      chips: [`Up to ${MAX_CERTIFICATE_ACCOUNTS} accounts`, 'Public link', 'Shareable image'],
      media: {
        kind: 'hero',
        component: hero('OverviewHero'),
        alt: 'A performance certificate rises in, its net profit counts up and a verified seal stamps on'
      }
    },
    {
      label: 'Choose',
      title: 'Pick your accounts and a time range',
      body: `Choose up to ${MAX_CERTIFICATE_ACCOUNTS} accounts in one currency, then any range from today to last month. Days run on UTC time.`,
      chips: ['Masters and slaves', 'Today to last month'],
      media: {
        kind: 'hero',
        component: hero('ChooseHero'),
        alt: 'Three accounts in one currency are ticked, then the time range moves to This Week'
      }
    },
    {
      label: 'Verify',
      title: 'Built from your real closed trades',
      body: 'Net profit, win rate, trades taken, your best day and your profit streak are read straight from your closed trades. Nobody types them in.',
      chips: ['Net profit', 'Win rate', 'Best day', 'Profit streak'],
      media: {
        kind: 'hero',
        component: hero('VerifyHero'),
        alt: 'The certificate is built from closed trades, then win rate, trades taken, profit streak and best day appear'
      }
    },
    {
      label: 'Share',
      title: 'Share it anywhere',
      body: 'Each certificate gets a public page and an image. Copy the link, download the image, or post it to X, LinkedIn, Facebook, WhatsApp or Telegram.',
      media: { kind: 'hero', component: hero('ShareHero'), alt: "The certificate's public link is copied and the share buttons appear" }
    }
  ],
  // Opens the dialog itself: /dashboard is where most people already are. No menu chip for the same reason.
  cta: { label: 'Create a certificate', route: `/dashboard?${CERTIFICATE_OPEN_PARAM}` },
  hintTarget: HINT_TARGETS.certificateButton,
  configFlag: 'certificates'
};
